import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  QualificationTrainingProviderType,
  QualificationTrainings,
  QualificationTrainingsStatus,
  RoleAction,
  RolePermissionSubject,
} from '@wilson/interfaces';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { firstValueFrom, map, Observable, tap } from 'rxjs';
import { TrainingQualificationsV2ListService } from '../../../service/training-qualifications-v2-list.service';
import { TrainingQualificationsDeleteOrCancelModalComponent } from '../training-qualifications-delete-or-cancel-modal/training-qualifications-delete-or-cancel-modal.component';
import { TrainingQualificationsGateway } from '@wilson/qualification-v2/common';
import { captureException } from '@sentry/angular';
import { NzMessageService } from 'ng-zorro-antd/message';

@Component({
  selector: 'wilson-training-qualification-delete-trigger',
  standalone: true,
  imports: [CommonModule, NzButtonModule, NzTooltipModule, TranslatePipe],
  templateUrl: './training-qualification-delete-trigger.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrainingQualificationDeleteTriggerComponent implements OnInit {
  private readonly trainingQualificationsV2ListService = inject(
    TrainingQualificationsV2ListService,
  );
  private readonly nzModalService = inject(NzModalService);
  private readonly translateService = inject(TranslateService);
  private readonly trainingQualificationsGateway = inject(
    TrainingQualificationsGateway,
  );
  private readonly toastrService = inject(NzMessageService);

  @Output() refresh = new EventEmitter<number>();

  public can = inject(AbilityServiceSignal).can;
  public readonly RoleAction = RoleAction;
  public readonly RolePermissionSubject = RolePermissionSubject;

  protected selectedQualificationTrainings$!: Observable<
    QualificationTrainings[]
  >;
  protected readonly areDigitalProviders: WritableSignal<boolean> =
    signal(false);
  protected readonly areNonDigitalProviders: WritableSignal<boolean> =
    signal(true);

  ngOnInit(): void {
    this.selectedQualificationTrainings$ =
      this.trainingQualificationsV2ListService.selectedQualificationTrainings$.pipe(
        map((qualificationTrainings) => {
          return qualificationTrainings.filter((qualificationTraining) => {
            const statusToExclude = [
              QualificationTrainingsStatus.declined,
              QualificationTrainingsStatus.cancelled,
              QualificationTrainingsStatus.qualificationCreated,
              QualificationTrainingsStatus.resultRejected,
            ];
            return !statusToExclude.includes(qualificationTraining.status);
          });
        }),
        tap((qualificationTrainings) => {
          if (qualificationTrainings.length) {
            this.areDigitalProviders.set(
              qualificationTrainings.every(
                (qualificationTraining) =>
                  qualificationTraining
                    .organizationalUnitQualificationTrainingProvider
                    .qualificationTrainingProvider.providerType ===
                  QualificationTrainingProviderType.Digital,
              ),
            );
            this.areNonDigitalProviders.set(
              qualificationTrainings.every(
                (qualificationTraining) =>
                  qualificationTraining
                    .organizationalUnitQualificationTrainingProvider
                    .qualificationTrainingProvider.providerType !==
                  QualificationTrainingProviderType.Digital,
              ),
            );
          }
        }),
      );
  }

  public showTrainingQualificationDeleteOrCancelModal(
    selectedQualificationTrainings: QualificationTrainings[],
  ): void {
    const areMixedProviders =
      !this.areDigitalProviders() && !this.areNonDigitalProviders();

    const title = areMixedProviders
      ? this.translateService.instant(
          'page.training-qualifications.modal.title.mixed',
        )
      : this.areDigitalProviders()
        ? this.translateService.instant(
            'page.training-qualifications.modal.title.digital',
          )
        : this.translateService.instant(
            'page.training-qualifications.modal.title.non-digital',
          );
    const confirmText = areMixedProviders
      ? this.translateService.instant(
          'page.training-qualifications.modal.button.mixed',
        )
      : this.areDigitalProviders()
        ? this.translateService.instant(
            'page.training-qualifications.modal.button.digital',
          )
        : this.translateService.instant(
            'page.training-qualifications.modal.button.non-digital',
          );

    const modalRef = this.nzModalService.create({
      nzContent: TrainingQualificationsDeleteOrCancelModalComponent,
      nzTitle: title,
      nzCancelText: this.translateService.instant('general.cancel'),
      nzOkText: confirmText,
      nzOkDanger: true,
      nzMaskClosable: false,
      nzOnOk: () => {
        return this.deleteOrCancelQualificationTrainings(
          selectedQualificationTrainings,
        );
      },
    });
    if (modalRef.componentInstance) {
      modalRef.componentInstance.areDigitalProviders =
        this.areDigitalProviders();
      modalRef.componentInstance.areNonDigitalProviders =
        this.areNonDigitalProviders();
      modalRef.componentInstance.selectedQualificationTrainings =
        selectedQualificationTrainings;
    }
  }

  private async deleteOrCancelQualificationTrainings(
    selectedQualificationTrainings: QualificationTrainings[],
  ): Promise<void> {
    const digitalQualificationTrainingIds: string[] = [];
    const nonDigitalQualificationTrainingIds: string[] = [];
    selectedQualificationTrainings.forEach((qualificationTraining) => {
      if (
        qualificationTraining.organizationalUnitQualificationTrainingProvider
          .qualificationTrainingProvider.providerType ===
        QualificationTrainingProviderType.Digital
      ) {
        digitalQualificationTrainingIds.push(qualificationTraining.id);
      } else {
        nonDigitalQualificationTrainingIds.push(qualificationTraining.id);
      }
    });

    try {
      if (digitalQualificationTrainingIds.length) {
        await firstValueFrom(
          this.trainingQualificationsGateway.cancelTrainingQualifications(
            digitalQualificationTrainingIds,
          ),
        );
      }
      if (nonDigitalQualificationTrainingIds.length) {
        await firstValueFrom(
          this.trainingQualificationsGateway.deleteTrainingQualifications(
            nonDigitalQualificationTrainingIds,
          ),
        );
      }
    } catch (error) {
      captureException(error);
      this.toastrService.error(
        this.translateService.instant('general.something_went_wrong'),
      );
    }
    this.refresh.emit(new Date().getTime());
  }
}
